import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IconFlask,
  IconFlaskOff,
  IconDnaOff,
  IconFolders,
  IconFoldersOff,
  IconPill,
  IconPillOff,
  IconCalendarX,
  IconCalendarTime,
  IconCalendar,
  IconDna,
  IconShieldHeart,
  IconChevronRight,
  IconChevronDown,
  IconChevronUp,
  IconSparkles,
  IconArrowUp,
  IconArrowDown,
} from "@tabler/icons-react";
import IconVaccination from "@/components/icons/IconVaccination";
import IconPaw from "@/components/icons/IconPaw";
import { MButton } from "@/components/ui/atoms/MButtons";
import { LabInsightsLanding, ParameterStatus } from "@/types/lab-insights";
import { MedicineTimingRuleType, PillboxItem } from "@/types/pillbox";
import { TrackingConfig, VitalsParameter, VitalsRange } from "@/types/vitals";
import {
  getBpRange,
  getClassificationRange,
  getGlucoseRange,
} from "@/utils/vitalsRanges";
import { ReminderTypeCounts } from "@/types/reminders";
import pillGradientIcon from "@/assets/member-detail/pillbox-pill-gradient.png";
import lifebuoyGradientIcon from "@/assets/member-detail/pillbox-lifebuoy-gradient.png";
import styles from "./MemberDetailCards.module.css";

interface DocumentTypeCounts {
  prescriptions?: number;
  labReports?: number;
  dischargeSummaries?: number;
  medicalReceipts?: number;
  insurances?: number;
  identity?: number;
  healthMemories?: number;
}

/* ---------------------------------------------------------------------- */
/* Maatra Summary (self-view only)                                        */
/* ---------------------------------------------------------------------- */

export const MaatraSummaryCard: React.FC<{
  summary: string;
  updatedAt?: string | null;
  onClick: () => void;
}> = ({ summary, updatedAt, onClick }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const formattedDate = updatedAt
    ? new Date(updatedAt).toLocaleString("en-GB", {
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <div className={styles.summaryCard}>
      <div className={styles.summaryHeader} onClick={onClick}>
        <div className={styles.summaryHeaderLeft}>
          <div className={styles.summaryIconBadge}>
            <IconSparkles size={12} color="#4C189A" />
          </div>
          <span className={styles.summaryTitle}>
            {t("memberDetail.cards.maatraSummary.title")}
          </span>
        </div>
        {formattedDate && (
          <span className={styles.summaryUpdatedAt}>
            {t("memberDetail.cards.maatraSummary.updated", {
              date: formattedDate,
            })}
          </span>
        )}
      </div>
      <div className={styles.summaryBodyWrap}>
        <p
          className={`${styles.summaryBody} ${
            expanded ? "" : styles.summaryBodyClamped
          }`}
        >
          {summary}
        </p>
        <span
          className={styles.summaryReadMore}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
        >
          {expanded
            ? t("memberDetail.cards.maatraSummary.readLess")
            : t("memberDetail.cards.maatraSummary.readMore")}
        </span>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------------- */
/* Lab trends card                                                        */
/* ---------------------------------------------------------------------- */

export const LabTrendsCard: React.FC<{
  landing: LabInsightsLanding | null;
  onClick: () => void;
  /** Empty state has nothing to view yet — "Add blood report" should open
   *  the document upload flow instead of navigating to the (empty) Lab
   *  Trends page. */
  onEmptyClick: () => void;
}> = ({ landing, onClick, onEmptyClick }) => {
  const { t } = useTranslation();
  const pinned = landing?.pinnedParameters ?? [];
  const isEmpty = pinned.length === 0;

  return (
    <div
      className={`${styles.card} ${styles.cardClickable}`}
      onClick={isEmpty ? onEmptyClick : onClick}
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIcon}>
            <IconFlask size={24} />
          </div>
          <div className={styles.cardTitleGroup}>
            <span className={styles.cardTitle}>
              {t("memberDetail.cards.labTrends.title")}
            </span>
            {!isEmpty && (
              <span className={styles.cardCaption}>
                {t("memberDetail.cards.labTrends.pinnedCount", {
                  count: pinned.length,
                })}
              </span>
            )}
          </div>
        </div>
        {!isEmpty && (
          <div className={styles.cardChevron}>
            <IconChevronRight size={16} />
          </div>
        )}
      </div>

      {isEmpty ? (
        <div className={styles.emptyCard}>
          <div className={styles.emptyStateMessage}>
            <IconFlaskOff size={24} className={styles.emptyStateIcon} />
            <p className={styles.emptyStateText}>
              {t("memberDetail.cards.labTrends.emptyMessage")}
            </p>
          </div>
          <MButton
            variant="filled"
            size="sm"
            onClick={(e) => {
              // Same double-fire risk as PillboxCard's empty state — the
              // card wrapper is also onClick={onEmptyClick} here.
              e.stopPropagation();
              onEmptyClick();
            }}
            className={styles.emptyStateButton}
          >
            {t("memberDetail.cards.labTrends.addCta")}
          </MButton>
        </div>
      ) : (
        <div className={`${styles.paramGrid} ${styles.paramGridWrap}`}>
          {pinned.slice(0, 4).map((param) => {
            const reading = param.latestReading;
            // Trust the backend's own `flagged` boolean (plus explicit
            // borderline statuses) rather than treating every non-NORMAL
            // status as flagged — UNKNOWN (no reference range available)
            // is a distinct grey state, not red, matching the real Lab
            // Insights page (MLabParameterCard).
            const flagged =
              reading?.flagged ||
              reading?.status === ParameterStatus.BORDERLINE_LOW ||
              reading?.status === ParameterStatus.BORDERLINE_HIGH;
            const dotClass = !reading
              ? null
              : flagged
              ? styles.paramDotFlagged
              : reading.status === ParameterStatus.NORMAL
              ? styles.paramDotNormal
              : styles.paramDotUnknown;
            return (
              <div
                className={`${styles.paramCard} ${styles.paramCardLg} ${styles.paramCardHalf}`}
                key={param.canonicalParameterId}
              >
                <div
                  className={`${styles.paramLabelRow} ${styles.paramLabelRowCentered}`}
                >
                  <span className={styles.paramLabel}>
                    {param.canonicalName}
                  </span>
                  {dotClass && (
                    <span className={`${styles.paramDot} ${dotClass}`} />
                  )}
                </div>
                <div
                  className={`${styles.paramValueRow} ${styles.paramValueRowInline}`}
                >
                  <span
                    className={`${styles.paramValue} ${
                      flagged
                        ? styles.paramValueFlagged
                        : styles.paramValueNormal
                    }`}
                  >
                    {reading?.parsedResult ?? reading?.rawResult ?? "--"}
                  </span>
                  {reading?.unit && (
                    <span className={styles.paramUnit}>{reading.unit}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ---------------------------------------------------------------------- */
/* Vitals tracker card                                                    */
/* ---------------------------------------------------------------------- */

const VITAL_LABEL_KEYS: Partial<Record<VitalsParameter, string>> = {
  [VitalsParameter.BP]: "bloodPressure",
  [VitalsParameter.GLUCOSE]: "bloodSugar",
  [VitalsParameter.WEIGHT]: "weight",
  [VitalsParameter.HEIGHT]: "height",
};

const isValueInRange = (value: number, range?: [number, number]) =>
  !range || (value >= range[0] && value <= range[1]);

const isVitalFlagged = (
  parameter: VitalsParameter,
  reading:
    | { values?: Record<string, any>; recordedRange?: VitalsRange }
    | undefined,
  range: VitalsRange | undefined
): boolean | null => {
  const values = reading?.values;
  if (!values) return null;
  // Judge against the range in force when the reading was saved
  const savedRange = getClassificationRange(reading, range);
  if (parameter === VitalsParameter.BP) {
    if (values.systolic == null || values.diastolic == null) return null;
    const bpRange = getBpRange(savedRange);
    return !(
      isValueInRange(values.systolic, bpRange.systolic) &&
      isValueInRange(values.diastolic, bpRange.diastolic)
    );
  }
  if (parameter === VitalsParameter.GLUCOSE) {
    if (values.value == null) return null;
    const categoryRange = getGlucoseRange(values.category, savedRange);
    return !isValueInRange(values.value, categoryRange);
  }
  return null;
};

const formatVitalValue = (
  parameter: VitalsParameter,
  values: Record<string, any> | undefined
): string => {
  if (!values) return "--";
  if (parameter === VitalsParameter.BP) {
    return values.systolic != null && values.diastolic != null
      ? `${values.systolic}/${values.diastolic}`
      : "--";
  }
  if (values.value != null) return String(values.value);
  return "--";
};

const VITAL_UNITS: Partial<Record<VitalsParameter, string>> = {
  [VitalsParameter.BP]: "mmHg",
  [VitalsParameter.GLUCOSE]: "mg/dL",
  [VitalsParameter.WEIGHT]: "Kg",
  [VitalsParameter.HEIGHT]: "cm",
};

export const VitalsTrackerCard: React.FC<{
  config: TrackingConfig | null;
  /** Shows a growth arrow next to Height — only meaningful for children,
   *  who grow; adults' height doesn't change so callers should pass false. */
  heightIncreased?: boolean;
  /** Shows an up/down trend arrow next to Weight — applies to everyone,
   *  adult or child, since weight can move either direction. */
  weightTrend?: "up" | "down" | null;
  onClick: () => void;
}> = ({ config, heightIncreased, weightTrend, onClick }) => {
  const { t } = useTranslation();
  const enabled = config?.enabledParameters ?? [];
  const isEmpty = enabled.length === 0;

  return (
    <div className={`${styles.card} ${styles.cardClickable}`} onClick={onClick}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIcon}>
            <IconDna size={24} />
          </div>
          <div className={styles.cardTitleGroup}>
            <span className={styles.cardTitle}>
              {t("memberDetail.cards.vitalsTracker.title")}
            </span>
            {!isEmpty && (
              <span className={styles.cardCaption}>
                {t("memberDetail.cards.vitalsTracker.trackedCount", {
                  count: enabled.length,
                })}
              </span>
            )}
          </div>
        </div>
        {!isEmpty && (
          <div className={styles.cardChevron}>
            <IconChevronRight size={16} />
          </div>
        )}
      </div>

      {isEmpty ? (
        <div className={styles.emptyCard}>
          <div className={styles.emptyStateMessage}>
            <IconDnaOff size={24} className={styles.emptyStateIcon} />
            <p className={styles.emptyStateText}>
              {t("memberDetail.cards.vitalsTracker.emptyMessage")}
            </p>
          </div>
          <MButton
            variant="filled"
            size="sm"
            onClick={(e) => {
              // Card wrapper is also onClick={onClick} — without this, a
              // single tap fires it twice (button, then bubbling to the
              // card), racing two navigate() calls.
              e.stopPropagation();
              onClick();
            }}
            className={styles.emptyStateButton}
          >
            {t("memberDetail.cards.vitalsTracker.addCta")}
          </MButton>
        </div>
      ) : (
        <div className={styles.paramGrid}>
          {enabled.map((parameter) => {
            const reading = config?.latestReadings?.[parameter];
            const flagged = isVitalFlagged(
              parameter,
              reading,
              config?.ranges?.[parameter]
            );
            const labelKey = VITAL_LABEL_KEYS[parameter];
            return (
              <div className={styles.paramCard} key={parameter}>
                <div className={styles.paramLabelRow}>
                  <span
                    className={`${styles.paramLabel} ${styles.paramLabelWrap}`}
                  >
                    {labelKey
                      ? t(`memberDetail.cards.vitalsTracker.${labelKey}`)
                      : parameter}
                  </span>
                  {parameter === VitalsParameter.HEIGHT && heightIncreased && (
                    <IconArrowUp size={16} className={styles.paramTrendIcon} />
                  )}
                  {parameter === VitalsParameter.WEIGHT &&
                    weightTrend &&
                    (weightTrend === "up" ? (
                      <IconArrowUp
                        size={16}
                        className={styles.paramTrendIcon}
                      />
                    ) : (
                      <IconArrowDown
                        size={16}
                        className={styles.paramTrendIcon}
                      />
                    ))}
                  {flagged !== null && (
                    <span
                      className={`${styles.paramDot} ${
                        flagged ? styles.paramDotFlagged : styles.paramDotNormal
                      }`}
                    />
                  )}
                </div>
                <div className={styles.paramValueRow}>
                  <span
                    className={`${styles.paramValue} ${
                      flagged
                        ? styles.paramValueFlagged
                        : styles.paramValueNormal
                    }`}
                  >
                    {formatVitalValue(parameter, reading?.values)}
                  </span>
                  <span className={styles.paramUnit}>
                    {VITAL_UNITS[parameter]}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ---------------------------------------------------------------------- */
/* Records hero card                                                      */
/* ---------------------------------------------------------------------- */

const RECORD_LABEL_KEYS: { key: keyof DocumentTypeCounts; labelKey: string }[] =
  [
    { key: "prescriptions", labelKey: "prescriptions" },
    { key: "labReports", labelKey: "reports" },
    { key: "dischargeSummaries", labelKey: "procedures" },
    { key: "identity", labelKey: "idDocuments" },
    { key: "healthMemories", labelKey: "healthMemories" },
    { key: "insurances", labelKey: "insurance" },
    { key: "medicalReceipts", labelKey: "bills" },
  ];

export const RecordsHeroCard: React.FC<{
  counts: DocumentTypeCounts | null;
  onClick: () => void;
}> = ({ counts, onClick }) => {
  const { t } = useTranslation();
  const breakdown = RECORD_LABEL_KEYS.map(({ key, labelKey }) => ({
    count: counts?.[key] ?? 0,
    label: t(`memberDetail.cards.records.${labelKey}`),
  })).filter((item) => item.count > 0);
  const total = breakdown.reduce((sum, item) => sum + item.count, 0);
  const isEmpty = total === 0;

  return (
    <div className={styles.heroCard} onClick={onClick}>
      {isEmpty ? (
        <>
          <p className={styles.heroEmptyTitle}>
            {t("memberDetail.cards.records.emptyTitle")}
          </p>
          <div className={styles.heroEmptyField}>
            <span className={styles.heroEmptySubtitle}>
              {t("memberDetail.cards.records.emptySubtitle")}
            </span>
            <MButton
              variant="filled"
              size="sm"
              onClick={(e) => {
                // Card wrapper is also onClick={onClick} — without this, a
                // single tap fires it twice (button, then bubbling to the
                // card), racing two navigate() calls.
                e.stopPropagation();
                onClick();
              }}
              className={styles.emptyStateButton}
            >
              {t("memberDetail.cards.records.addCta")}
            </MButton>
          </div>
          <IconFoldersOff className={styles.heroDecoration} />
        </>
      ) : (
        <>
          <p className={styles.heroTitle}>{total}</p>
          <div className={styles.heroLabelRow}>
            <span className={styles.heroSubtitle}>
              {t("memberDetail.cards.records.title")}
            </span>
            <div className={styles.heroChevronBadge}>
              <IconChevronRight size={16} color="#fff" />
            </div>
          </div>
          <span className={styles.heroCaption}>
            {t("memberDetail.cards.records.includesPrefix")}{" "}
            {breakdown.map((item) => `${item.count} ${item.label}`).join(", ")}
          </span>
          <IconFolders className={styles.heroDecoration} />
        </>
      )}
    </div>
  );
};

/* ---------------------------------------------------------------------- */
/* Pillbox card                                                           */
/* ---------------------------------------------------------------------- */

export const PillboxCard: React.FC<{
  items: PillboxItem[] | null;
  memberDisplayName: string;
  /** True when viewing your own profile — titles as "Your Pillbox" instead
   *  of the grammatically-wrong "You's Pillbox". */
  isSelf?: boolean;
  onClick: () => void;
  /** The SOS stat tile expands its own chip list in place, same as
   *  "Scheduled" — but tapping an individual chip in that expanded SOS
   *  list navigates to the SOS page instead of just being plain text. */
  onSosClick: () => void;
}> = ({ items, memberDisplayName, isSelf, onClick, onSosClick }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<"scheduled" | "sos" | null>(null);
  const list = items ?? [];
  const isEmpty = list.length === 0;
  const sos = list.filter(
    (item) => item.timingRuleType === MedicineTimingRuleType.CONDITIONAL
  );
  const scheduled = list.filter(
    (item) => item.timingRuleType !== MedicineTimingRuleType.CONDITIONAL
  );

  // "Scheduled medications" / "SOS medications" — break onto two lines with
  // "medications" always on its own line, per Figma.
  const splitLabel = (label: string) => {
    const lastSpace = label.lastIndexOf(" ");
    return lastSpace === -1
      ? [label, ""]
      : [label.slice(0, lastSpace), label.slice(lastSpace + 1)];
  };
  const [scheduledLabelFirst, scheduledLabelLast] = splitLabel(
    t("memberDetail.cards.pillbox.scheduledMedications")
  );
  const [sosLabelFirst, sosLabelLast] = splitLabel(
    t("memberDetail.cards.pillbox.sosMedications")
  );

  return (
    <div className={`${styles.card} ${styles.cardClickable}`} onClick={onClick}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIcon}>
            <IconPill size={24} />
          </div>
          <span className={styles.cardTitle}>
            {isSelf ? "Your" : `${memberDisplayName}’s`}{" "}
            {t("memberDetail.cards.pillbox.titleSuffix")}
          </span>
        </div>
        {!isEmpty && (
          <div className={styles.cardChevron}>
            <IconChevronRight size={16} />
          </div>
        )}
      </div>

      {isEmpty ? (
        <div className={styles.emptyCard}>
          <div className={styles.emptyStateMessage}>
            <IconPillOff size={24} className={styles.emptyStateIcon} />
            <p className={styles.emptyStateText}>
              {t("memberDetail.cards.pillbox.emptyMessage")}
            </p>
          </div>
          <MButton
            variant="filled"
            size="sm"
            onClick={(e) => {
              // Card wrapper (.card) is also onClick={onClick} — without
              // this, a single tap fires onClick twice (button, then
              // bubbling to the card), racing two navigate() calls and
              // sometimes corrupting the backUrl the second one captures.
              e.stopPropagation();
              onClick();
            }}
            className={styles.emptyStateButton}
          >
            {t("memberDetail.cards.pillbox.addCta")}
          </MButton>
        </div>
      ) : (
        <>
          <div className={styles.pillboxStats}>
            <div
              className={`${styles.pillboxStatCard} ${
                styles.pillboxScheduled
              } ${expanded === "scheduled" ? styles.pillboxStatActive : ""} ${
                scheduled.length === 0 ? styles.pillboxStatCardDisabled : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (scheduled.length === 0) return;
                setExpanded((prev) =>
                  prev === "scheduled" ? null : "scheduled"
                );
              }}
            >
              <div className={styles.pillboxStatValueRow}>
                <span
                  className={`${styles.pillboxStatValue} ${styles.pillboxScheduledText}`}
                >
                  {scheduled.length}
                </span>
                {/* No caret when there's nothing to expand into. */}
                {scheduled.length > 0 &&
                  (expanded === "scheduled" ? (
                    <IconChevronUp size={16} color="#36116D" />
                  ) : (
                    <IconChevronDown size={16} color="#36116D" />
                  ))}
              </div>
              <span
                className={`${styles.pillboxStatLabel} ${styles.pillboxScheduledText}`}
              >
                {scheduledLabelFirst}
                <br aria-hidden />
                {scheduledLabelLast}
              </span>
              <img
                src={pillGradientIcon}
                alt=""
                className={styles.pillboxDecoration}
              />
            </div>
            <div
              className={`${styles.pillboxStatCard} ${styles.pillboxSos} ${
                expanded === "sos" ? styles.pillboxStatActive : ""
              } ${sos.length === 0 ? styles.pillboxStatCardDisabled : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                if (sos.length === 0) return;
                setExpanded((prev) => (prev === "sos" ? null : "sos"));
              }}
            >
              <div className={styles.pillboxStatValueRow}>
                <span
                  className={`${styles.pillboxStatValue} ${styles.pillboxSosText}`}
                >
                  {sos.length}
                </span>
                {/* No caret when there's nothing to expand into. */}
                {sos.length > 0 &&
                  (expanded === "sos" ? (
                    <IconChevronUp size={16} color="#804D23" />
                  ) : (
                    <IconChevronDown size={16} color="#804D23" />
                  ))}
              </div>
              <span
                className={`${styles.pillboxStatLabel} ${styles.pillboxSosText}`}
              >
                {sosLabelFirst}
                <br aria-hidden />
                {sosLabelLast}
              </span>
              <img
                src={lifebuoyGradientIcon}
                alt=""
                className={styles.pillboxDecoration}
              />
            </div>
          </div>
          {expanded && (
            <>
              <div className={styles.divider} />
              <div className={styles.medChips}>
                {(expanded === "scheduled" ? scheduled : sos).map((item) => (
                  <span
                    className={`${styles.medChip} ${
                      expanded === "sos" ? styles.medChipSos : ""
                    }`}
                    key={item.id}
                    onClick={
                      expanded === "sos"
                        ? (e) => {
                            e.stopPropagation();
                            onSosClick();
                          }
                        : undefined
                    }
                  >
                    {item.name}
                  </span>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

/* ---------------------------------------------------------------------- */
/* Reminders card                                                         */
/* ---------------------------------------------------------------------- */

export const RemindersCard: React.FC<{
  counts: ReminderTypeCounts | null;
  onClick: () => void;
}> = ({ counts, onClick }) => {
  const { t } = useTranslation();
  const categories: {
    key: keyof ReminderTypeCounts;
    icon: React.ReactNode;
  }[] = [
    { key: "appointment", icon: <IconCalendar size={20} /> },
    { key: "medication", icon: <IconPill size={20} /> },
    { key: "vitals", icon: <IconDna size={20} /> },
    { key: "insurance", icon: <IconShieldHeart size={20} /> },
    { key: "vaccination", icon: <IconVaccination size={20} /> },
    { key: "petCare", icon: <IconPaw size={20} /> },
  ];
  const activeCategories = categories.filter(
    ({ key }) => (counts?.[key] ?? 0) > 0
  );
  const isEmpty = activeCategories.length === 0;

  return (
    <div className={`${styles.card} ${styles.cardClickable}`} onClick={onClick}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIcon}>
            <IconCalendarTime size={24} />
          </div>
          <span className={styles.cardTitle}>
            {t("memberDetail.cards.reminders.title")}
          </span>
        </div>
        {!isEmpty && (
          <div className={styles.cardChevron}>
            <IconChevronRight size={16} />
          </div>
        )}
      </div>

      {isEmpty ? (
        <div className={styles.emptyCard}>
          <div className={styles.emptyStateMessage}>
            <IconCalendarX size={24} className={styles.emptyStateIcon} />
            <p className={styles.emptyStateText}>
              {t("memberDetail.cards.reminders.emptyMessage")}
            </p>
          </div>
          <MButton
            variant="filled"
            size="sm"
            onClick={(e) => {
              // Card wrapper is also onClick={onClick} — without this, a
              // single tap fires it twice (button, then bubbling to the
              // card), racing two navigate() calls.
              e.stopPropagation();
              onClick();
            }}
            className={styles.emptyStateButton}
          >
            {t("memberDetail.cards.reminders.addCta")}
          </MButton>
        </div>
      ) : (
        <div className={styles.reminderBadges}>
          {activeCategories.map(({ key, icon }) => (
            <div className={styles.reminderBadge} key={key}>
              <div className={styles.reminderIconWrap}>
                {icon}
                <span className={styles.reminderCount}>{counts?.[key]}</span>
              </div>
              <span className={styles.reminderLabel}>
                {key === "vitals" ? (
                  <>
                    {t("memberDetail.cards.reminders.vitals")}
                    <br />
                    {t("memberDetail.cards.reminders.vitalsTrackingSuffix")}
                  </>
                ) : (
                  t(`memberDetail.cards.reminders.${key}`)
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
